import { FeatureCardProps } from "../components/featureSection/components/featureCard/featureCard.component";
import { VideoSectionProps } from "../components/videoSection/videoSection.component";

interface MerchantStaticProps {
  heroSection: {
    title: string;
    gradientSubHeading: string;
    baseSubHeading: string;
    microSubHeading: string;
    badgeText: string;
    supportingText: string;
    appStoreDownload: {
      title: string;
      link: string;
      showIcon: boolean;
    };
    googlePlayDownload: {
      title: string;
      link: string;
      showIcon: boolean;
    };
  };
  featureSection: {
    heading: string;
    subHeading: string;
    features: Omit<FeatureCardProps, "cardIndex">[];
  };
  customerTestimonialsSection: {
    testimonials: {
      id: number;
      quote: string;
      description: string;
      author: string;
      rating: number;
      authorStoreName: string;
      authorStoreType: string;
      authorAvatar: string;
    }[];
    heading: string;
    subHeading: string;
    points?: Array<string>;
  };
  FAQSection: {
    heading: string;
    faqItems: {
      id?: number;
      question: string;
      answer: string;
    }[];
  };
  helpSection: {
    title: string;
    subtitle?: string;
    cards: {
      title: string;
      points: string[];
      isPrimary?: boolean;
    }[];
  };
  signupSection: {
    title: string;
    subtitle: string;
    buttonLabel: string;
    buttonLink: string;
    badgeText?: string;
  };
  videoSection: VideoSectionProps;
  stepsSection: {
    title: string;
    description: string;
    steps: {
      id: number;
      iconUrl: string;
      title: string;
      description: string;
    }[];
  };
}

const merchantStatic: MerchantStaticProps = {
  heroSection: {
    title: "Increase Repeat Customers for Your Retail Business",
    gradientSubHeading: "Customers visit once -",
    baseSubHeading: "but don’t come back?",
    microSubHeading:
      "Fydo is a simple, AI-powered customer loyalty platform for retail businesses that helps shop owners increase repeat customers and attract new ones through loyalty programs and WhatsApp marketing. Simple app. No complicated setup - works with your existing POS system or even if you don’t use one.",
    supportingText:
      "No setup required • Works for all types of shops • Get started in minutes",
    badgeText: "Rated 4.8 on Playstore",
    appStoreDownload: {
      title: "Download App",
      link: "/download-now",
      showIcon: false,
    },
    googlePlayDownload: {
      title: "Request a Callback",
      link: "/contact",
      showIcon: false,
    },
  },
  featureSection: {
    heading: "A Complete Customer Growth Engine for Your Shop",
    subHeading:
      "Fydo gives you the tools to bring customers back, attract new ones, and increase repeat business - all from one simple platform built for retail shops.",
    features: [
      {
        id: 1,
        titleStart: "Turn Every ",
        titleGrad: { text: "Customer into a ", gradient: false },
        titleEnd: { text: "Repeat Customer", gradient: true },
        description:
          "Give your customers a reason to come back again and again. Create smart rewards based on visits, spending, timing, or customer type - so you stay in control of your customer relationships and never lose a customer after the first visit.",
        isReversed: false,
        animation: "get_customer",
        bullets: [
          "Reward customers based on visits, spending, or special days",
          "Create personalized offers for new, repeat, and inactive customers",
          "Run time-based and festival campaigns easily",
        ],
      },
      {
        id: 2,
        titleStart: "Bring ",
        titleGrad: "New Customers",
        titleEnd: "to Your Shop Every Day",
        description:
          "Promote your offers and stay connected with your customers through free WhatsApp marketing. Reach your customers instantly, bring them back, and attract new customers to your shop without extra effort.",
        isReversed: true,
        animation: "bring_more",
        bullets: [
          "Run WhatsApp marketing campaigns in minutes",
          "Share offers and updates directly with new customers in your area",
          "Bring back inactive customers and attract new ones",
        ],
      },
      {
        id: 3,
        titleStart: "Know Your ",
        titleGrad: { text: "Customers and ", gradient: false },
        titleEnd: { text: "Grow Your Sales", gradient: true },
        description:
          "Understand customer visits, repeat behavior, and campaign performance so you can make smarter decisions and increase sales.",
        isReversed: false,
        animation: "understand",
        bullets: [
          "Track repeat customers and visits",
          "See which campaigns bring more customers",
          "Make better decisions to grow your business",
        ],
      },
    ],
  },
  customerTestimonialsSection: {
    testimonials: [
      {
        id: 1,
        quote: " Fydo helped us beat the local competition",
        description:
          "There are many stores around us selling the same items, and it was tough to stand out. But after we started using Fydo, things changed. The loyalty rewards gave us an edge customers started choosing our shop over others because they felt more valued here",
        author: "Samarjit Sahoo",
        rating: 4.9,
        authorStoreName: "Galaxy General Store ",
        authorStoreType: "Retail & Grocery Store",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 2,
        quote: "Now our connection with customers goes beyond the counter.",
        description:
          "We now connect with our customers beyond the shop. Fydo’s personalized offers and rewards have made our engagement more meaningful and effective.",
        author: "Avinash Nayak",
        rating: 4.8,
        authorStoreName: "Golden Bakery",
        authorStoreType: "Bakery Store",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 3,
        quote: "People keep coming back that’s the difference Fydo made.",
        description:
          "Fydo has helped us turn first time buyers into loyal customers. The loyalty program keeps people coming back, and we’ve seen a good increase in repeat visits.",
        author: " Patitapabana Aluk",
        rating: 4.8,
        authorStoreName: "Maa Dakhineswari Variety Store",
        authorStoreType: "Retail & Grocery Store",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 4,
        quote: "More walk-ins, more visibility all thanks to Fydo.",
        description:
          "Since onboarding with Fydo, we’ve noticed more new customers walking in thanks to the app’s discovery feature and local campaigns",
        author: "Pathani Pradhan",
        rating: 4.8,
        authorStoreName: "Tara Tarini Glass & Plywood",
        authorStoreType: "Hardware Store",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 5,
        quote: "It’s so easy and it actually works!",
        description:
          "The platform is so easy to use. Without spending extra on advertising, we’re able to reward loyalty and grow our regular customer base.",
        author: "Md. Irshad Ansari",
        rating: 4.8,
        authorStoreName: "Irshad Variety Store",
        authorStoreType: "Retail & Grocery Store",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 6,
        quote: "Loyalty with real results not just discounts.",
        description:
          "Fydo has added real value to our business. With its smart loyalty system, we've seen a rise in repeat customers and quicker conversions. It’s not just about offers it’s about building lasting relationships, and Fydo makes that easy and effective.",
        author: "Sohel",
        rating: 4.8,
        authorStoreName: "Big byte",
        authorStoreType: "Food & Dining",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 7,
        quote: "This is how loyalty should be done.",
        description:
          "Fydo has transformed how we connect with our customers. The loyalty features helped us not only retain our existing buyers but also attract new footfall. It’s simple, effective, and has definitely boosted our sales and brand presence.",
        author: "Monika Mohapatra",
        rating: 4.8,
        authorStoreName: "BLUEBERRY",
        authorStoreType: "Food & Dining",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 8,
        quote: "People now come for more than just food",
        description:
          "Since we started using Fydo, our repeat customers have significantly increased. The loyalty program encourages diners to come back, and the exclusive offers help us stand out in a competitive market. It's been a valuable addition to our restaurant’s growth strategy.",
        author: "Gourav Mohapatra",
        rating: 4.8,
        authorStoreName: "Delicacies On Plate",
        authorStoreType: "Food & Dining",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 9,
        quote: "Even in healthcare, loyalty matters and Fydo gets it.",
        description:
          "Fydo has helped us strengthen patient relationships through their smart loyalty features. Regular clients now feel more valued, and referrals have grown through word of mouth. It's a unique tool for a diagnostic lab like ours, adding a human touch to healthcare services.",
        author: "Gopal Krushna Mohapatra",
        rating: 4.8,
        authorStoreName: "Vijaya Clinical Laboratory",
        authorStoreType: "Doctor Laboratory",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 10,
        quote: "Old customers returned, and new ones followed.",
        description:
          "With Fydo, we've seen a noticeable increase in repeat visits and customer engagement. The loyalty program not only brings back our old customers but also encourages them to refer others. It’s been a game changer for our optical business.",
        author: "SK Sakil",
        rating: 4.8,
        authorStoreName: "Netra Vision",
        authorStoreType: "Optical Store",
        authorAvatar: "/images/avatar.png",
      },
      {
        id: 11,
        quote: " Fydo showed us what our customers really want.",
        description:
          "Getting feedback and insights through Fydo has helped us improve service and offer exactly what our customers want.",
        author: "Shreejit Panda",
        rating: 4.8,
        authorStoreName: "Biggies Burger ",
        authorStoreType: "Food & Dining",
        authorAvatar: "/images/avatar.png",
      },
    ],
    heading: "Trusted by Shop Owners Who Are Growing Their Business",
    subHeading:
      "Retailers across India use Fydo to increase repeat customers, bring customers back, and grow their sales - without complicated setups or heavy discounts.",
    points: [
      "1L+ users",
      "4.8 rating on Play Store",
      "Trusted by 10000+ retail businesses",
    ],
  },
  FAQSection: {
    heading: "Common Queries if you are a Shop Owner :",
    faqItems: [
      {
        question: "How can Fydo help me increase repeat customers?",
        answer:
          "Fydo helps you bring customers back by running loyalty programs, rewards, and targeted offers. You can give cashback or discounts based on visits, spending, or special occasions, so customers have a reason to return to your shop again and again.",
      },
      {
        question: "Will Fydo help increase footfall and sales in my shop?",
        answer:
          "Yes. Fydo helps increase footfall by bringing back existing customers and attracting new ones through WhatsApp marketing and campaigns. More repeat visits naturally lead to higher sales and better customer retention.",
      },
      {
        question:
          "Will Fydo be helpful to large stores or to small stores as well?",
        answer:
          "Fydo is aimed at any kind of retail business. In case you operate a small local store or a chain of stores, it is a shop owner application that can grow along with your business. Fydo is favored by many small retailers who can use it as a simple and user-friendly application that does not require technical skill and additional manpower to operate in  shopkeepers.",
      },
      {
        question: "Is Fydo easy to use for shop owners?",
        answer:
          "Yes, Fydo is designed for shopkeepers. You don’t need any technical knowledge. You can set up your account, create offers, and start using it within minutes.",
      },
      {
        question: "Do I need a POS system to use Fydo?",
        answer:
          "No. Fydo works with your existing POS system if you have one, but you can also use it without any POS. It is designed to work for all types of retail shops.",
      },
      {
        question:
          "Can I run offers and promotions without spending a lot of money?",
        answer:
          "Yes. With Fydo, you control your rewards. You can create smart offers that increase repeat customers without giving heavy discounts or spending on ads.",
      },
      {
        question: "Can Fydo help me get new customers also?",
        answer:
          "Yes. Along with loyalty programs, Fydo helps you attract new customers through WhatsApp marketing and by encouraging existing customers to come back more often.",
      },
      {
        question: "What kind of businesses can use Fydo?",
        answer:
          "Fydo is built for all types of retail businesses - from small shops and local stores to growing brands. Whether you run a grocery store, clothing shop, or restaurant, Fydo can help you grow.",
      },
      {
        question: "What insights will I get from Fydo?",
        answer:
          "You can track customer visits, spending, repeat customers, and campaign performance. This helps you understand what is working and make better decisions to grow your business.",
      },
      {
        question: "How quickly can I start seeing results?",
        answer:
          "Most shop owners start seeing repeat visits and better engagement within a few days of using Fydo, especially after running their first offers or campaigns.",
      },
      {
        question: "Why should I choose Fydo over other loyalty apps?",
        answer:
          "Fydo is simple, flexible, and built specifically for shop owners. It not only helps you run loyalty programs but also helps you bring customers back, attract new ones, and grow your business - all in one platform.",
      },
    ],
  },
  helpSection: {
    title: "Everything You Need to Grow Your Retail Business",
    subtitle:
      "Fydo helps shop owners increase repeat customers, improve customer engagement, and grow their business - all from one simple customer loyalty platform designed for retail shops.",
    cards: [
      {
        title: "Understand Your Customers Better",
        points: [
          "See how often your customers visit your shop",
          "Understand what they buy and how much they spend",
          "Identify your repeat and high-value customers",
          "Spot customers who have stopped visiting",
          "Track customer behavior to make better decisions",
        ],
        isPrimary: true,
      },
      {
        title: "Build Loyalty and Bring Customers Back",
        points: [
          "Run loyalty programs that increase repeat customers",
          "Share offers and updates through WhatsApp marketing",
          "Create rewards for new, repeat, and inactive customers",
          "Run festival, timing, and special campaigns easily",
          "Stay connected with your customers and build relationships",
        ],
        isPrimary: true,
      },
      {
        title: "Increase Sales and Grow Your Business",
        points: [
          "See which offers and campaigns bring more customers",
          "Avoid unnecessary discounts and improve profits",
          "Identify peak times and slow days",
          "Take smarter actions based on customer insights",
          "Grow your retail business with better decisions",
        ],
        isPrimary: true,
      },
    ],
  },
  signupSection: {
    title:
      "Join thousands of shopkeepers using Fydo to grow faster, smarter, and stronger.",
    buttonLabel: "Talk to Our Support Team",
    buttonLink: "/contact",
    badgeText: "Your First Step Towards Business Growth",
    subtitle:
      "We're here to help you every step of the way. Let's grow your business together.",
  },
  videoSection: {
    heading:
      "More Competition, More Choices - How Do You Bring Customers Back?",
    description:
      "Today, customers have many options and can easily switch shops. Fydo helps you stay connected with your customers, bring them back to your shop, and grow your sales with simple tools that are easy to use.",
    videoId: "2EL2FQonB30",
    thumbnailSrc: "/images/fydo_video_thumbnail.jpg",
  },
  stepsSection: {
    title: "Start Bringing Customers Back in Just 3 Simple Steps",
    description:
      "No technical knowledge. No extra effort. Set up Fydo in minutes and start growing your customer base from day one.",
    steps: [
      {
        id: 1,
        iconUrl: "/icons/steps/download.svg",
        title: "Download the Fydo Partner App",
        description:
          "Get started in minutes. Download the app, sign up, and you’re ready to go - no complicated setup needed.",
      },
      {
        id: 2,
        iconUrl: "/icons/steps/award.svg",
        title: "Create Your Own Rewards",
        description:
          "Set up loyalty offers based on visits, spending, or special occasions - exactly the way you want for your shop.",
      },
      {
        id: 3,
        iconUrl: "/icons/steps/arrow-right.svg",
        title: "Invite Customers & Watch Them Come Back",
        description:
          "Share your offers with customers and start seeing more repeat visits, stronger loyalty, and higher sales.",
      },
    ],
  },
};

export default merchantStatic;
